import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronIcon,
  DropdownButton,
  DropdownContainer,
  DropdownItem,
  DropdownMenu,
  DropdownMenuInner,
  Label,
  RemoveButton,
  SelectedItem,
  SelectedItemsContainer,
} from './style';

const MultiSelectDropdown = ({
  options = [],
  selectedItems = [],
  onSelectionChange,
  label = 'Select options',
  placeholder = 'Select...',
  isKeepDropdownOpen = true,
  selectedLabel = 'items',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalSelectedItems, setInternalSelectedItems] = useState(selectedItems);
  const dropdownRef = useRef(null);

  // Update internal state when selectedItems prop changes
  useEffect(() => {
    setInternalSelectedItems(selectedItems);
  }, [selectedItems]);

  // Handle clicking outside to close dropdown - only if isKeepDropdownOpen is false
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isKeepDropdownOpen]);

  const toggleDropdown = (e) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling
    setIsOpen(!isOpen);
  };

  const handleItemSelect = (selectedOption, e) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling

    const isAlreadySelected = internalSelectedItems.some(
      (item) => item.value === selectedOption.value
    );

    let updatedSelection;
    if (isAlreadySelected) {
      // Remove item if already selected
      updatedSelection = internalSelectedItems.filter(
        (item) => item.value !== selectedOption.value
      );
    } else {
      // Add item if not selected
      updatedSelection = [...internalSelectedItems, selectedOption];
    }

    setInternalSelectedItems(updatedSelection);

    // Call parent callback with updated selection
    if (onSelectionChange) {
      onSelectionChange(updatedSelection);
    }

    // Close dropdown only if isKeepDropdownOpen is false
    if (!isKeepDropdownOpen) {
      setIsOpen(false);
    }
  };

  const handleRemoveItem = (valueToRemove, e) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling

    const updatedSelection = internalSelectedItems.filter((item) => item.value !== valueToRemove);

    setInternalSelectedItems(updatedSelection);

    // Call parent callback with updated selection
    if (onSelectionChange) {
      onSelectionChange(updatedSelection);
    }
  };

  const getDisplayText = (selectedLabel) => {
    if (internalSelectedItems.length === 0) {
      return placeholder;
    }
    if (internalSelectedItems.length === 1) {
      return internalSelectedItems[0].label;
    }
    return `${internalSelectedItems.length} ${selectedLabel} selected`;
  };

  const isItemSelected = (option) => {
    return internalSelectedItems.some((item) => item.value === option.value);
  };

  const handleDoneSelection = (e) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling
    setIsOpen(false);
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      <Label>{label}</Label>
      <DropdownButton
        onClick={toggleDropdown}
        type="button" // Explicitly set type to button
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span>{getDisplayText(selectedLabel)}</span>
        <ChevronIcon
          isOpen={isOpen}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M6.293 9.293a1 1 0 011.414 0L10 11.586l2.293-2.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </ChevronIcon>
      </DropdownButton>

      {isOpen && options.length > 0 && (
        <DropdownMenu role="menu" aria-orientation="vertical">
          <DropdownMenuInner>
            {options.map((option) => (
              <DropdownItem
                key={option.value}
                onClick={(e) => handleItemSelect(option, e)} // Pass event to handler
                type="button" // Explicitly set type to button
                role="menuitem"
                style={{
                  backgroundColor: isItemSelected(option) ? '#E6B471' : 'transparent',
                  fontWeight: isItemSelected(option) ? '600' : '400',
                  color: isItemSelected(option) ? '#000' : '#fff',
                }}
              >
                {isItemSelected(option) ? '✓ ' : ''}
                {option.label}
              </DropdownItem>
            ))}
            {/* Optional: Add a Done button when keeping dropdown open */}
            {isKeepDropdownOpen && (
              <DropdownItem
                onClick={handleDoneSelection}
                type="button" // Explicitly set type to button
                role="menuitem"
                style={{
                  borderTop: '1px solid #494C6A',
                  marginTop: '4px',
                  fontWeight: '600',
                  color: '#7aeaa7',
                }}
              >
                Done
              </DropdownItem>
            )}
          </DropdownMenuInner>
        </DropdownMenu>
      )}

      {internalSelectedItems.length > 0 && (
        <SelectedItemsContainer>
          {internalSelectedItems.map((item) => (
            <SelectedItem key={item.value}>
              <span>{item.label}</span>
              <RemoveButton
                type="button" // Explicitly set type to button
                onClick={(e) => handleRemoveItem(item.value, e)} // Pass event to handler
                aria-label={`Remove ${item.label}`}
              >
                ×
              </RemoveButton>
            </SelectedItem>
          ))}
        </SelectedItemsContainer>
      )}
    </DropdownContainer>
  );
};

export default MultiSelectDropdown;
